import {useRef} from 'react';
import {useTranslations} from 'next-intl';
import cx from 'classnames';
import Container from '@/container';
import {Row, Col} from '@/grid';
import {Grid, VideoModalButton} from '@/content/features/shared';
import {SectionPaddingHalf} from '@/section';
import {H2, P} from '@/typography';
import {withNeutral700, withNeutral900} from '@/hocs/withColors';
import {withHeadingEyebrow, withUppercase} from '@/hocs/withFontType';
import {withMarginBottom2xlToLg} from '@/hocs/withSpacing';
import PictureWrapper from '@/ui/images-wrapper';
import ModalWrapper from '@/ui/modal-wrapper';
import Testimonial from '@/ui/testimonial';
import styles from './styles.module.scss';

const EyebrowNeutral700 = withNeutral700(withUppercase(withHeadingEyebrow(P)));
const H2Neutral900 = withMarginBottom2xlToLg(withNeutral900(H2));

const imgPath = '/static/images/customers/';

type VideoConfig = {
  key: string;
  imageUrl: string;
  videoId: string;
  gtm: string;
  companyLogoName?: string;
  companyLogoAlt?: string;
};

const videos: VideoConfig[] = [
  {key: 'video1', imageUrl: 'beautytech-thumb', videoId: 'fq6z8dx5wx', gtm: 'customers-video-beautytech', companyLogoName: 'beauty-tech-logo', companyLogoAlt: 'BeautyTech'},
  {key: 'video2', imageUrl: 'we-are-social-thumb', videoId: 'qs1onvq1hs', gtm: 'customers-video-we-are-social', companyLogoName: 'we-are-social-logo', companyLogoAlt: 'We Are Social'},
  {key: 'video3', imageUrl: 'peakmade-thumb', videoId: 'sdkaxpzx59', gtm: 'customers-video-peakmade', companyLogoName: 'peakmade-logo', companyLogoAlt: 'PeakMade'},
  {key: 'video4', imageUrl: 'coral-casino-thumb', videoId: 'pvsaeqnc5i', gtm: 'customers-video-coral-casino', companyLogoName: 'coral-casino-logo', companyLogoAlt: 'Coral Casino'},
  {key: 'video5', imageUrl: 'belmond-thumb', videoId: 'up0336mfxc', gtm: 'customers-video-belmond', companyLogoName: 'belmond-logo', companyLogoAlt: 'Belmond'}
];

type TestimonialsSectionProps = {
  /** When true, applies object-fit: cover + height: 100% to all media inside video grid items */
  fillMedia?: boolean;
};

const TestimonialsSection = ({fillMedia = false}: TestimonialsSectionProps) => {
  const t = useTranslations('customers.testimonialsSection');

  const ref0 = useRef<HTMLButtonElement | null>(null);
  const ref1 = useRef<HTMLButtonElement | null>(null);
  const ref2 = useRef<HTMLButtonElement | null>(null);
  const ref3 = useRef<HTMLButtonElement | null>(null);
  const ref4 = useRef<HTMLButtonElement | null>(null);
  const buttonRefs = [ref0, ref1, ref2, ref3, ref4];

  const companyLogo = (
    <picture>
      <source
        srcSet={`${imgPath}pharmacy2u-logo.webp 1x, ${imgPath}pharmacy2u-logo_2x.webp 2x`}
        type="image/webp"
      />
      <img
        src={`${imgPath}pharmacy2u-logo.png`}
        srcSet={`${imgPath}pharmacy2u-logo.png 1x, ${imgPath}pharmacy2u-logo_2x.png 2x`}
        alt={t('quote1.companyLogoAlt')}
        loading="lazy"
      />
    </picture>
  );

  return (
    <SectionPaddingHalf className={cx(styles.section, {[styles['section--cover-media']]: fillMedia})}>
      <Container>
        <Grid fluid>
          <Row>
            <Col xs={12}>
              <EyebrowNeutral700>{t('eyebrow')}</EyebrowNeutral700>
              <H2Neutral900>{t('title')}</H2Neutral900>
            </Col>
          </Row>
          <Row>
            <Col xs={12}>
              <div className={styles['videos-grid']}>
                {videos.map((video, index) => (
                  <ModalWrapper key={video.key} variant="modal-btn-wrapper">
                    {video.companyLogoName && (
                      <div className={styles['video-overlay']}>
                        <div className={styles['video-overlay__logo']}>
                          <img
                            src={`${imgPath}${video.companyLogoName}.png`}
                            alt={video.companyLogoAlt}
                            loading="lazy"
                          />
                        </div>
                      </div>
                    )}
                    {t(`${video.key}.caption`) && (
                      <p className={`heading-3 ${styles['video-caption']}`}>{t(`${video.key}.caption`)}</p>
                    )}
                    <PictureWrapper
                      imgPath={imgPath}
                      imageUrl={video.imageUrl}
                      imageTypeFallback="jpg"
                      altImage={t(`${video.key}.imageAlt`)}
                      loading="lazy"
                      width={480}
                      height={270}
                      onClick={() => buttonRefs[index].current?.click()}
                    />
                    <VideoModalButton
                      ref={buttonRefs[index]}
                      openName="openList2"
                      videoId={video.videoId}
                      variant="image-green"
                      customAttributes={{'data-gtm': video.gtm}}
                      transcript={t(`${video.key}.transcript`)}
                    />
                  </ModalWrapper>
                ))}
                <Testimonial
                  variant="card"
                  companyLogo={companyLogo}
                  label={t('quote1.label')}
                  byLineName={t('quote1.byLineName')}
                  byLineTitle={t('quote1.byLineTitle')}>
                  {t('quote1.quote')}
                </Testimonial>
              </div>
            </Col>
          </Row>
        </Grid>
      </Container>
    </SectionPaddingHalf>
  );
};

export default TestimonialsSection;
